import { useState } from 'react';
import { Helmet } from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../shared/featureFlags/flagNames';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../shared/subscriptions';
import { window } from '../../globals';
import { useIsFeatureEnabled } from '../../helpers/features';
import CallToAction from '../CallToAction';
import Flex from '../generic/Flex';
import PageHeadingBreadcrumbs from '../generic/PageHeadingBreadcrumbs';
import type { Organization } from '../../types';

import { BackfillCard } from './Backfills/BackfillCard';
import BillingTermCard from './BillingTermCard';
import ChangeOrgComponent from './ChangeOrgCard/ChangeOrgCard';
import ChangeSubscriptionComponent from './ChangeSubscriptionCard';
import ContributorsCard from './ContributorsCard';
import ContributorCountComponent from './CustomContribCountCard';
import CustomProvidedBuildsQueueCard from './CustomProvidedBuildsQueueCard';
import { DangerZoneCard } from './DangerZoneCard/DangerZoneCard';
import FeatureControlCard from './FeatureControlCard';
import { FullOrganizationPackageCacheRebuildCard } from './FullOrganizationPackageCacheRebuildCard';
import OrgInfoCard from './OrgInfoCard';
import ReanalyzeQuickImportCard from './ReanalyzeQuickImportCard';
import ToggleInternalOrgCard from './ToggleInternalOrgCard/ToggleInternalOrgCard';
import UserInfoComponent from './UserInfoCard';

const AdminDashboard: VoidFunctionComponent = () => {
  const [org, setOrg] = useState<Organization>(window.user?.organization as Organization);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const hasBinaryDecomp = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  return (
    <>
      <CallToAction>
        <PageHeadingBreadcrumbs
          truncateAt={80}
          crumbs={[
            {
              to: '/admin',
              text: 'Admin Tools',
            },
            {
              text: window.user?.organization?.title ?? 'Organization',
            },
          ]}
        />
      </CallToAction>
      <div className="container margin-top admin-dashboard">
        <Helmet title="Admin Dashboard" />
        {generalError ? <div className="alert alert-error">{generalError}</div> : null}
        <div className="alert">
          Stuff on this page can have catastrophic effects if used incorrectly. Make sure you know what
          you&#39;re doing.
        </div>
        <div className="content">
          <div className="content--main">
            <Flex direction="column" grow={1} align="flex-start" gap={12}>
              <ChangeOrgComponent />
              <ChangeSubscriptionComponent org={org} />
              {(org.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME || hasBinaryDecomp) &&
                <BillingTermCard />}
              <ContributorCountComponent org={org} setOrg={setOrg} />
              <FeatureControlCard setError={setGeneralError} />
              {!window.on_prem && <CustomProvidedBuildsQueueCard />}
              <ReanalyzeQuickImportCard />
              <FullOrganizationPackageCacheRebuildCard org={org} />
              <BackfillCard />
              <ToggleInternalOrgCard />
              <DangerZoneCard />
            </Flex>
          </div>
          <div className="content--side">
            <UserInfoComponent />
            <OrgInfoCard />
            <ContributorsCard />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
